import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { fetchTeamLeagueData, leagueQueryString } from "../utils/teamLeagueData";
import { buildTeamSummary } from "../utils/teamSummary";
import { seasonLabel } from "../utils/leagueCatalog";
import { EmptyState } from "./ui/Pills";
import TeamCrest, { useTeamColor } from "./ui/TeamCrest";
import ShareButton from "./ui/ShareButton";
import { alpha, shade } from "../utils/color";

const RESULT = {
  W: { bg: "#16a34a", label: "Win" },
  D: { bg: "#9ca3af", label: "Draw" },
  L: { bg: "#dc2626", label: "Loss" },
  F: { bg: "#111", label: "Forfeit" },
};

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const dateLabel = (key, opts = { weekday: "short", month: "short", day: "numeric" }) => {
  if (!key) return "TBD";
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-US", { ...opts, timeZone: "America/Toronto" });
};

const timeLabel = (t) => {
  if (!t) return "";
  const [h, m = "00"] = String(t).split(":");
  const hour = Number(h);
  return `${hour % 12 || 12}:${m.slice(0, 2)} ${hour >= 12 ? "PM" : "AM"}`;
};

const Crest = (props) => <TeamCrest {...props} />;

const FormChip = ({ r, size = 24 }) => (
  <Box
    title={RESULT[r]?.label}
    sx={{ width: size, height: size, borderRadius: 1.5, bgcolor: RESULT[r]?.bg, boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.25)", color: "#fff", fontWeight: 800, fontSize: size * 0.5, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
  >
    {r}
  </Box>
);

const Panel = ({ title, action, children, sx }) => (
  <Box component="section" sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 3, p: { xs: 2, sm: 2.5 }, ...sx }}>
    {title && (
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.5, gap: 1 }}>
        <Typography component="h2" sx={{ fontWeight: 900, fontSize: "1.05rem", color: "#111" }}>
          {title}
        </Typography>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const Tile = ({ label, value, tone }) => (
  <Box sx={{ bgcolor: "#fff", border: "1px solid #ececec", borderRadius: 3, px: 2, py: 1.5 }}>
    <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>{label}</Typography>
    <Typography sx={{ fontSize: { xs: "1.3rem", sm: "1.6rem" }, fontWeight: 900, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: tone || "#111", lineHeight: 1.2 }}>{value}</Typography>
  </Box>
);

const GameRow = ({ g }) => {
  const f = g.fixture;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "84px minmax(0,1fr) auto", alignItems: "center", gap: 1.5, py: 1.1, borderBottom: "1px solid #f3f3f3", "&:last-of-type": { borderBottom: 0 } }}>
      <Box>
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#111" }}>{dateLabel(f.date)}</Typography>
        <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>{g.played ? (g.home ? "Home" : "Away") : timeLabel(f.time) || (g.home ? "Home" : "Away")}</Typography>
      </Box>
      <Box
        component={g.opponent.id != null ? Link : "div"}
        to={g.opponent.id != null ? `/team/${g.opponent.id}` : undefined}
        sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, color: "inherit", textDecoration: "none", "&:hover span": { textDecoration: "underline" } }}
      >
        <Crest team={g.opponent} size={26} />
        <Typography component="span" noWrap sx={{ fontWeight: 700, fontSize: "0.9rem", minWidth: 0 }}>
          {g.home ? "vs " : "@ "}
          {g.opponent.name || "TBD"}
        </Typography>
      </Box>
      {g.played ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
            {g.result === "F" ? "FF" : `${g.goalsFor}–${g.goalsAgainst}`}
          </Typography>
          <FormChip r={g.result} size={22} />
        </Box>
      ) : (
        <Typography sx={{ fontSize: "0.75rem", color: "#888", textAlign: "right", display: { xs: "none", sm: "block" } }}>{f.field || ""}</Typography>
      )}
    </Box>
  );
};

/** Public team profile: table position, form, results, upcoming games and squad stats. */
const TeamPage = () => {
  const { teamId } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    fetchTeamLeagueData(teamId)
      .then((s) => !cancelled && setState({ loading: false, ...s }))
      .catch((e) => {
        console.error("Team page:", e);
        if (!cancelled) setState({ loading: false, team: null });
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const summary = useMemo(
    () => (state.team ? buildTeamSummary(state.team.id, state.fixtures) : null),
    [state.team, state.fixtures]
  );
  const accent = useTeamColor(state.team?.logo_url) || "#27272a";

  if (state.loading) {
    return (
      <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Skeleton variant="circular" width={88} height={88} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={140} />
            <Skeleton width={260} height={44} />
          </Box>
        </Box>
        <Skeleton variant="rounded" height={90} sx={{ mt: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={260} sx={{ mt: 2, borderRadius: 3 }} />
      </Box>
    );
  }

  if (!state.team) {
    return <EmptyState title="Team not found" body="This team may have been renamed or removed." action={{ label: "See standings", to: "/standings" }} sx={{ mt: 6 }} />;
  }

  const { team, league, players } = state;
  const row = summary.row;
  const qs = leagueQueryString(league);
  const scorers = [...players].sort((a, b) => (b.goals || 0) - (a.goals || 0) || (b.assists || 0) - (a.assists || 0) || String(a.name).localeCompare(String(b.name)));
  const next = summary.next;

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 4 }, pb: 6 }}>
      <Box
        component="header"
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 4,
          color: "#fff",
          p: { xs: 2, sm: 3 },
          background: `linear-gradient(135deg, ${shade(accent, 0.25)} 0%, ${shade(accent, 0.62)} 100%)`,
          boxShadow: `0 18px 40px -24px ${alpha(accent, 0.9)}`,
          transition: "background 400ms ease",
          "&::after": {
            content: '""',
            position: "absolute",
            right: -60,
            top: -60,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.18), rgba(255,255,255,0) 70%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, sm: 3 }, position: "relative", zIndex: 1 }}>
          <Box sx={{ width: { xs: 72, sm: 104 }, height: { xs: 72, sm: 104 }, boxSizing: "border-box", p: { xs: 1, sm: 1.5 }, borderRadius: 4, bgcolor: "#fff", boxShadow: "0 6px 18px rgba(0,0,0,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
            <Crest team={team} size="100%" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", opacity: 0.75 }}>
              {[league.city, league.division, league.season && seasonLabel(league.season)].filter(Boolean).join(" · ")}
            </Typography>
            <Typography component="h1" sx={{ fontSize: { xs: "1.7rem", sm: "2.6rem" }, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.05 }}>
              {team.name}
            </Typography>
            {summary.position && row?.gp > 0 && (
              <Typography sx={{ mt: 0.5, opacity: 0.85, fontWeight: 600, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                {ordinal(summary.position)} of {summary.teamCount} · {row.points} pts
                {summary.position > 1 ? ` · ${summary.leaderPoints - row.points} behind the leader` : " · top of the table"}
              </Typography>
            )}
            {summary.form.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.5, mt: 1 }} aria-label="Last five results">
                {summary.form.map((r, i) => (
                  <FormChip key={i} r={r} />
                ))}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ position: "relative", zIndex: 1, mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <ShareButton kind="team" data={{ team, league, summary, accent }} label="Share team card" />
        </Box>
      </Box>

      {row && (
        <Box sx={{ mt: 3, display: "grid", gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(6, 1fr)" }, gap: 1.25 }}>
          <Tile label="Played" value={row.gp} />
          <Tile label="Record" value={`${row.wins}-${row.draws}-${row.losses}`} />
          <Tile label="Points" value={row.points} />
          <Tile label="Scored" value={row.goalsFor} />
          <Tile label="Conceded" value={row.goalsAgainst} />
          <Tile label="Goal diff" value={row.goalDifference > 0 ? `+${row.goalDifference}` : row.goalDifference} tone={row.goalDifference > 0 ? "#16a34a" : row.goalDifference < 0 ? "#dc2626" : undefined} />
        </Box>
      )}

      {next && (
        <Box sx={{ mt: 2, bgcolor: shade(accent, 0.7), color: "#fff", borderRadius: 3, p: { xs: 2, sm: 2.5 }, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.6 }}>Next game</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: "1.2rem", mt: 0.25 }}>
              {next.home ? "vs" : "@"} {next.opponent.name || "TBD"}
            </Typography>
            <Typography sx={{ opacity: 0.75, fontSize: "0.88rem", display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
              {dateLabel(next.fixture.date, { weekday: "long", month: "short", day: "numeric" })}
              {next.fixture.time ? ` · ${timeLabel(next.fixture.time)}` : ""}
              {next.fixture.field ? " · " : ""}
              {next.fixture.field && (
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, whiteSpace: "nowrap" }}>
                  <PlaceOutlinedIcon sx={{ fontSize: 15 }} />
                  {next.fixture.field}
                </Box>
              )}
            </Typography>
          </Box>
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, p: 0.75, display: "flex" }}>
            <Crest team={next.opponent} size={48} />
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" }, gap: 2, alignItems: "start" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <Panel
            title="Results"
            action={
              <Button component={Link} to={`/schedule?${qs}&team=${team.id}`} size="small" sx={{ textTransform: "none", fontWeight: 700, color: "#111" }}>
                Full schedule →
              </Button>
            }
          >
            {summary.results.length ? summary.results.map((g) => <GameRow key={g.fixture.id} g={g} />) : <Typography sx={{ color: "#888", fontSize: "0.9rem" }}>No results yet.</Typography>}
          </Panel>
          {summary.upcoming.length > 0 && (
            <Panel title="Upcoming">
              {summary.upcoming.map((g) => (
                <GameRow key={g.fixture.id} g={g} />
              ))}
            </Panel>
          )}
        </Box>
        <Panel
          title="Squad"
          action={
            <Button component={Link} to={`/standings?${qs}`} size="small" sx={{ textTransform: "none", fontWeight: 700, color: "#111" }}>
              Standings →
            </Button>
          }
        >
          {scorers.length === 0 ? (
            <Typography sx={{ color: "#888", fontSize: "0.9rem" }}>Roster not published yet.</Typography>
          ) : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: "32px minmax(0,1fr) 32px 32px 32px", gap: 1, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.08em", color: "#999", textTransform: "uppercase", pb: 0.75, borderBottom: "1px solid #f0f0f0" }}>
                <span>#</span>
                <span>Player</span>
                <Box sx={{ textAlign: "center" }}>G</Box>
                <Box sx={{ textAlign: "center" }}>A</Box>
                <Box sx={{ textAlign: "center" }}>RC</Box>
              </Box>
              {scorers.map((p) => (
                <Box key={p.id} sx={{ display: "grid", gridTemplateColumns: "32px minmax(0,1fr) 32px 32px 32px", gap: 1, alignItems: "center", py: 0.8, borderBottom: "1px solid #f6f6f6", "&:last-of-type": { borderBottom: 0 }, fontSize: "0.88rem" }}>
                  <Box sx={{ color: "#999", fontVariantNumeric: "tabular-nums" }}>{p.jersey_number ?? "–"}</Box>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: "0.88rem" }}>{p.name}</Typography>
                  <Box sx={{ textAlign: "center", fontWeight: 800, color: p.goals ? "#b7860b" : "#bbb" }}>{p.goals || 0}</Box>
                  <Box sx={{ textAlign: "center", color: p.assists ? "#111" : "#bbb" }}>{p.assists || 0}</Box>
                  <Box sx={{ textAlign: "center", color: p.red_cards ? "#dc2626" : "#bbb" }}>{p.red_cards || 0}</Box>
                </Box>
              ))}
            </>
          )}
        </Panel>
      </Box>

      {summary.results.length > 0 && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button component={Link} to={`/insights?${qs}`} sx={{ textTransform: "none", fontWeight: 700, color: "#111" }}>
            Season insights for {league.division} →
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TeamPage;
